/**
 * src/lib/lazyPagina.ts
 * =============================================================================
 * Carga diferida de páginas que sobrevive a un despliegue.
 *
 * QUÉ CUBRE, Y QUÉ SE COMPROBÓ ANTES DE ESCRIBIRLO
 * -------------------------------------------------
 * Los ficheros del build llevan un hash en el nombre. Al publicar una versión
 * nueva esos nombres cambian, así que la pregunta obvia es: ¿qué le pasa a
 * quien tenía la aplicación abierta desde antes y navega a una página que se
 * carga bajo demanda? Su código viejo solo conoce los nombres viejos.
 *
 * En muchos alojamientos eso es una pantalla en blanco. **Aquí NO.** Medido
 * contra producción el 2026-08-22, después de publicar: los ficheros del
 * despliegue anterior seguían respondiendo 200, incluso saltándose la caché
 * del borde (`cf-cache-status: MISS`). Cloudflare Pages conserva los assets de
 * los despliegues anteriores en el dominio de producción.
 *
 * Así que esto NO arregla un fallo que estuviéramos teniendo. Es una red por
 * si esa conservación deja de cumplirse: si se purgan despliegues viejos, si
 * cambiamos de alojamiento, o simplemente si a alguien le falla la red justo
 * al pedir un fragmento. En esos casos, en vez de una pantalla en blanco sin
 * explicación, la página se recarga una vez y sigue.
 *
 * (Se deja escrito porque yo mismo di por hecho el fallo antes de medirlo, y
 * medirlo dijo lo contrario. El siguiente que lea esto merece el dato, no la
 * suposición.)
 *
 * CÓMO FUNCIONA
 * -------------
 * Si la carga falla, se recarga la página UNA vez. Al recargar, el navegador
 * pide de nuevo el index.html —que se sirve con `max-age=0, must-revalidate`,
 * comprobado— y con él llegan los nombres nuevos.
 *
 * El "una vez" es lo importante. Si el fallo fuera por falta de cobertura y no
 * por un despliegue, recargar en bucle sería una pantalla en blanco
 * parpadeando para siempre. La marca vive en `sessionStorage`: dura lo que la
 * pestaña, así que dos publicaciones en la misma sesión tienen cada una su
 * recarga.
 * =============================================================================
 */

import { lazy, type ComponentType } from 'react'

const MARCA = 'ploter:recarga-por-despliegue'

/**
 * Igual que `React.lazy`, pero recuperándose de un fallo de carga por
 * despliegue.
 *
 * @param cargar la función que hace el `import()` dinámico
 */
export function lazyPagina<T extends ComponentType<unknown>>(
  cargar: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const modulo = await cargar()
      // Cargó bien: se limpia la marca para que una futura publicación pueda
      // volver a recuperarse en esta misma pestaña.
      try {
        sessionStorage.removeItem(MARCA)
      } catch {
        /* modo privado sin storage — no pasa nada */
      }
      return modulo
    } catch (err) {
      // Sin storage no hay forma de saber si ya recargamos, así que se da por
      // hecho que sí: una pantalla de error es mala, un bucle infinito de
      // recargas es peor.
      const yaLoIntentamos = ((): boolean => {
        try {
          if (sessionStorage.getItem(MARCA) !== null) return true
          sessionStorage.setItem(MARCA, '1')
          return false
        } catch {
          return true
        }
      })()

      if (yaLoIntentamos) throw err

      window.location.reload()
      // La página se está yendo. Esta promesa no resuelve nunca a propósito:
      // devolver algo aquí haría que React pintase un instante de contenido
      // roto justo antes de la recarga.
      return new Promise<{ default: T }>(() => {})
    }
  })
}
